<template>
  <section class="benefits-tier-form">
    <div class="benefits-tier-field">
      <label class="benefits-tier-field-label" for="benefits-tier-name">
        {{ t("benefitsCenter-tiers-panel-name") }}
        <span class="benefits-required-asterisk">*</span>
      </label>
      <KvInput
        id="benefits-tier-name"
        :value="value.name"
        :maxlength="NAME_MAX_LENGTH"
        variant="outlined"
        size="large"
        :placeholder="t('benefitsCenter-tiers-panel-namePlaceholder')"
        @input="update('name', $event)"
      />
      <span class="benefits-tier-name-counter" :class="{ 'is-full': nameLength >= NAME_MAX_LENGTH }">{{ nameLength }}/{{ NAME_MAX_LENGTH }}</span>
    </div>

    <div class="benefits-tier-field">
      <span class="benefits-tier-field-label">
        {{ t("benefitsCenter-tiers-panel-description") }}
        <span class="benefits-required-asterisk">*</span>
      </span>
      <BenefitsRichText
        :value="value.description"
        :placeholder="t('benefitsCenter-tiers-panel-descriptionPlaceholder')"
        height="120px"
        @input="update('description', $event)"
      />
    </div>

    <div class="benefits-tier-field">
      <span class="benefits-tier-field-label">
        {{ t("benefitsCenter-tiers-panel-segment") }}
        <span class="benefits-required-asterisk">*</span>
      </span>
      <KvSelect
        :value="value.segmentId"
        :options="segmentOptions"
        filterable
        variant="outlined"
        size="large"
        full-width
        :placeholder="segmentsLoading ? t('benefitsCenter-tiers-panel-segmentLoading') : t('benefitsCenter-tiers-panel-segmentPlaceholder')"
        :search-placeholder="t('benefitsCenter-tiers-panel-segmentSearch')"
        :empty-text="t('benefitsCenter-tiers-panel-segmentEmpty')"
        @input="update('segmentId', $event)"
      >
        <template #option="{ option }">
          <span class="benefits-tier-segment-option">
            <span>{{ option.label }}</span>
            <small v-if="option.usedBy">{{ t("benefitsCenter-tiers-panel-segmentUsedBy", { name: option.usedBy }) }}</small>
          </span>
        </template>
      </KvSelect>
    </div>

    <div class="benefits-tier-field">
      <span class="benefits-tier-field-label">
        {{ t("benefitsCenter-tiers-panel-becoming") }}
        <span class="benefits-required-asterisk">*</span>
      </span>
      <BenefitsRichText
        :value="value.becomingDescription"
        :placeholder="t('benefitsCenter-tiers-panel-becomingPlaceholder')"
        height="160px"
        @input="update('becomingDescription', $event)"
      />
    </div>

    <div class="benefits-tier-field">
      <span class="benefits-tier-section-title">{{ t("benefitsCenter-tiers-panel-tierRewards") }}</span>
      <BenefitsTierRewardsGrid :rewards="tierRewards" />
      <p v-if="tierRewards.length" class="benefits-tier-field-hint">{{ t("benefitsCenter-tiers-panel-rewardsHint") }}</p>
    </div>
  </section>
</template>

<script>
import KvInput from "../../../components/inputs/kv-input.vue";
import KvSelect from "../../../components/inputs/kv-select.vue";
import BenefitsRichText from "../../shared/benefits-rich-text.vue";
import BenefitsTierRewardsGrid from "./benefits-tier-rewards-grid.vue";
import { NAME_MAX_LENGTH, rewardsOfTier } from "./tiers-utils";

export default {
  name: "BenefitsTierSettingsForm",
  components: {
    KvInput,
    KvSelect,
    BenefitsRichText,
    BenefitsTierRewardsGrid,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: { type: Object, required: true },
    tiers: { type: Array, default: () => [] },
    segments: { type: Array, default: () => [] },
    segmentsLoading: { type: Boolean, default: false },
    rewards: { type: Array, default: () => [] },
  },
  data() {
    return {
      NAME_MAX_LENGTH,
    };
  },
  computed: {
    nameLength() {
      return (this.value.name || "").length;
    },
    otherTiers() {
      return this.tiers.filter((tier) => tier.id !== this.value.id);
    },
    segmentOptions() {
      const usedBy = new Map(this.otherTiers.map((tier) => [String(tier.segmentId), tier.name]));

      return [...this.segments]
        .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
        .map((segment) => ({
          value: segment.id,
          label: segment.name,
          usedBy: usedBy.get(String(segment.id)) || "",
          disabled: usedBy.has(String(segment.id)),
        }));
    },
    tierRewards() {
      return rewardsOfTier(this.rewards, this.value.id);
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    update(field, fieldValue) {
      this.$emit("input", { ...this.value, [field]: fieldValue });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tier-form';

.benefits-tier-name-counter {
  align-self: flex-end;
  color: #6c737f;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  line-height: 1.2;
  margin-top: -8px;
}

.benefits-tier-name-counter.is-full {
  color: #b54708;
}

.benefits-tier-segment-option {
  display: flex;
  flex-direction: column;
  gap: 2px;

  small {
    color: #9da4ae;
    font-size: 12px;
  }
}
</style>
